import { writeFile, deleteFile } from '../../../../utils/api';

// Compute what file operations the workflow update would perform
export function computeChangeset(originalSteps, currentSteps) {
  // DEBUG: Log ALL steps before filtering
  console.group('[Changeset] ALL STEPS (before filter)');
  console.log('Total current steps:', currentSteps.length);
  currentSteps.forEach((s, idx) => {
    console.log(`Step ${idx}:`, {
      id: s.id,
      nodeId: s.nodeId,
      name: s.name,
      filepath: s.filepath,
      isStart: s.isStart,
      isEnd: s.isEnd,
      isLoopStart: s.isLoopStart,
      hasConfig: !!s.config,
      hasTemplate: !!s.config?.template,
      configKeys: s.config ? Object.keys(s.config) : []
    });
  });
  console.groupEnd();

  const isTemplateStep = (s) => s.nodeId !== 'start' && s.nodeId !== 'prompt' && s.nodeId !== 'response';

  const originalSkills = (originalSteps || []).filter(isTemplateStep);
  const currentSkills = currentSteps.filter(isTemplateStep);

  // DEBUG: Log filtered skills
  console.log('[Changeset] Original skills:', originalSkills.length);
  console.log('[Changeset] Current skills:', currentSkills.length);
  console.log('[Changeset] Current skills details:', currentSkills.map(s => ({
    id: s.id,
    nodeId: s.nodeId,
    name: s.name,
    filepath: s.filepath,
    hasTemplate: !!s.config?.template
  })));

  // Index by step ID for rename detection, and by filepath for file-level diff
  const originalById = {};
  originalSkills.forEach(s => { originalById[s.id] = s; });

  const currentById = {};
  currentSkills.forEach(s => { currentById[s.id] = s; });

  const filesToCreate = [];
  const filesToUpdate = [];
  const filesToRemove = [];
  const filesToRename = [];
  const claudeMdSkills = [];

  // Check current steps: create, update, or rename
  currentSkills.forEach(step => {
    const filepath = step.filepath;
    if (!filepath) {
      console.log('[Changeset] Skipping step without filepath:', { id: step.id, nodeId: step.nodeId, name: step.name });
      return;
    }

    const template = step.config?.template || '';
    const skillName = step.config?.skillName || step.nodeId;
    const title = step.config?.title || step.name;
    const description = step.config?.description || step.subtitle || '';

    claudeMdSkills.push({ name: skillName, title, description, filepath });

    const originalStep = originalById[step.id];
    if (!originalStep) {
      // Brand new step
      filesToCreate.push({ filepath, template, skillName, title, description });
    } else if (originalStep.filepath !== filepath) {
      // Same step but filepath changed (title rename)
      filesToRename.push({
        oldFilepath: originalStep.filepath,
        newFilepath: filepath,
        skillName,
        title,
        description,
        template,
        oldSkillName: originalStep.config?.skillName || originalStep.nodeId
      });
    } else if (originalStep.config?.template !== template) {
      // Same filepath, template content changed
      filesToUpdate.push({ filepath, template, skillName, title, description, previousTemplate: originalStep.config?.template });
    }
  });

  // Check for fully removed steps (not just renamed)
  const renamedOldPaths = new Set(filesToRename.map(r => r.oldFilepath));
  originalSkills.forEach(step => {
    if (!currentById[step.id] && !renamedOldPaths.has(step.filepath)) {
      filesToRemove.push({
        filepath: step.filepath,
        skillName: step.config?.skillName || step.nodeId,
        title: step.config?.title || step.name
      });
    }
  });

  return { filesToCreate, filesToUpdate, filesToRemove, filesToRename, claudeMdSkills };
}

// Get the skill directory path for deletion (e.g. '.claude/skills/foo/SKILL.md' → '.claude/skills/foo')
export function getSkillDir(filepath) {
  if (filepath && filepath.endsWith('/SKILL.md')) {
    return filepath.replace('/SKILL.md', '');
  }
  return filepath;
}

// Apply the changeset — write/rename/delete skill files on disk
export async function applyChangeset(changeset) {
  console.log('[applyChangeset] Starting with changeset:', changeset);
  const results = { created: [], updated: [], renamed: [], removed: [], errors: [] };

  // Create new skill files
  for (const f of changeset.filesToCreate) {
    try {
      console.log('[applyChangeset] Creating file:', f.filepath);
      const result = await writeFile(f.filepath, f.template);
      console.log('[applyChangeset] Created result:', result);
      results.created.push(f.filepath);
    } catch (err) {
      console.error('[applyChangeset] Create error:', err);
      results.errors.push({ action: 'create', filepath: f.filepath, error: err.message });
    }
  }

  // Update modified skill files
  for (const f of changeset.filesToUpdate) {
    try {
      await writeFile(f.filepath, f.template);
      results.updated.push(f.filepath);
    } catch (err) {
      results.errors.push({ action: 'update', filepath: f.filepath, error: err.message });
    }
  }

  // Rename: write new file, delete old folder
  for (const f of changeset.filesToRename) {
    try {
      await writeFile(f.newFilepath, f.template);
      await deleteFile(getSkillDir(f.oldFilepath));
      results.renamed.push({ from: f.oldFilepath, to: f.newFilepath });
    } catch (err) {
      results.errors.push({ action: 'rename', filepath: f.newFilepath, error: err.message });
    }
  }

  // Remove deleted skill folders
  for (const f of changeset.filesToRemove) {
    try {
      console.log('[applyChangeset] Removing file:', f.filepath);
      const skillDir = getSkillDir(f.filepath);
      console.log('[applyChangeset] Skill dir to delete:', skillDir);
      await deleteFile(skillDir);
      console.log('[applyChangeset] Removed successfully');
      results.removed.push(f.filepath);
    } catch (err) {
      console.error('[applyChangeset] Remove error:', err);
      results.errors.push({ action: 'remove', filepath: f.filepath, error: err.message });
    }
  }

  console.log('[applyChangeset] Completed. Results:', results);
  return results;
}
